import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";
import {
  LayoutDashboard,
  FileSignature,
  Send,
  ShieldCheck,
  Fingerprint,
  Users,
  UserCog,
  ScrollText,
  Settings,
  Code2,
  KeyRound,
  Search,
  Check,
  Minus,
  X,
  ChevronRight,
  Upload,
  Plus,
  BookOpen,
  Headphones,
} from "lucide-react";
 
function PermissionsMatrixStyles() {
  return (
    <style>{`
/* ---------------------------------------------------------------------- */
/* Tokens                                                                  */
/* ---------------------------------------------------------------------- */
 
.pm-app {
  --pm-bg: #f7f8fb;
  --pm-surface: #ffffff;
  --pm-border: #e7e9f0;
  --pm-text: #16192c;
  --pm-text-muted: #6b7183;
  --pm-text-faint: #9298a8;
  --pm-primary: #5b4bf5;
  --pm-primary-light: #eeecfe;
  --pm-primary-hover: #4a3ce0;
 
  --pm-green-bg: #e7f8ef;
  --pm-green-border: #b8ecce;
  --pm-green-text: #1a9d55;
 
  --pm-amber-bg: #fef6e0;
  --pm-amber-border: #fbe4a3;
  --pm-amber-text: #cc9412;
 
  --pm-red-bg: #fdeceb;
  --pm-red-border: #f8c6c1;
  --pm-red-text: #e0463c;
 
  --pm-radius-sm: 6px;
  --pm-radius-md: 10px;
  --pm-radius-lg: 14px;
 
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: var(--pm-text);
  background: var(--pm-bg);
  font-size: 13px;
  line-height: 1.45;
  box-sizing: border-box;
}
 
.pm-app *,
.pm-app *::before,
.pm-app *::after {
  box-sizing: border-box;
}
 
.pm-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #ffffff;
  border-bottom: 1px solid var(--pm-border);
}
 
.pm-tabs {
  display: flex;
  gap: 24px;
}
 
.pm-tab {
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  color: #64748b;
  padding-bottom: 10px;
  border-bottom: 2px solid transparent;
  cursor: pointer;
}
 
.pm-tab.active {
  color: var(--pm-primary);
  border-bottom-color: var(--pm-primary);
}
 
.pm-actions {
  display: flex;
  gap: 8px;
}
 
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  border-radius: var(--pm-radius-sm);
  padding: 7px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease;
}
 
.btn-primary {
  background: var(--pm-primary);
  color: #fff;
}
.btn-primary:hover {
  background: var(--pm-primary-hover);
}
 
.btn-ghost {
  background: #ffffff;
  border-color: var(--pm-border);
  color: var(--pm-primary);
}
.btn-ghost:hover {
  background: var(--pm-primary-light);
}
 
.btn-outline {
  background: #ffffff;
  border-color: var(--pm-border);
  color: #64748b;
}
.btn-outline:hover {
  border-color: var(--pm-primary);
  color: var(--pm-primary);
}
 
.btn-sm {
  padding: 5px 10px;
  font-size: 12px;
}
 
/* ---------------------------------------------------------------------- */
/* Layout                                                                  */
/* ---------------------------------------------------------------------- */
 
.pm-body {
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 14px 18px;
}
 
/* ---------------------------------------------------------------------- */
/* Sidebar                                                                 */
/* ---------------------------------------------------------------------- */
 
.pm-sidebar {
  flex: 0 0 240px;
  background: var(--pm-surface);
  border: 1px solid var(--pm-border);
  border-radius: var(--pm-radius-lg);
  padding: 14px;
  display: flex;
  flex-direction: column;
}
 
.pm-panel-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 12px;
}
 
.pm-search {
  position: relative;
  margin-bottom: 12px;
}
 
.pm-search-icon {
  position: absolute;
  top: 50%;
  left: 11px;
  transform: translateY(-50%);
  color: var(--pm-text-faint);
}
 
.pm-search input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e2e8f0;
  border-radius: var(--pm-radius-sm);
  font-size: 13px;
  font-family: inherit;
  color: #475569;
  background: var(--pm-bg);
  outline: none;
}
 
.pm-search input:focus {
  border-color: var(--pm-primary);
  background: var(--pm-surface);
}
 
.pm-category-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}
 
.pm-category-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 7px 8px;
  border-radius: var(--pm-radius-sm);
  border: 1px solid transparent;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
}
 
.pm-category-item:hover {
  background: var(--pm-bg);
}
 
.pm-category-item.active {
  background: var(--pm-primary-light);
  border-color: #d9d4fc;
}
 
.pm-category-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--pm-bg);
  color: var(--pm-text-muted);
}
 
.pm-category-item.active .pm-category-icon {
  background: #fff;
  color: var(--pm-primary);
}
 
.pm-category-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
 
.pm-category-label {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}
 
.pm-category-item.active .pm-category-label {
  color: var(--pm-primary);
}
 
.pm-category-desc {
  font-size: 11px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
 
.pm-category-chevron {
  align-self: center;
  color: var(--pm-primary);
  flex: 0 0 auto;
}
 
.pm-sidebar-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--pm-border);
  font-size: 11px;
  color: var(--pm-text-faint);
  flex-shrink: 0;
}
 
/* ---------------------------------------------------------------------- */
/* Main                                                                    */
/* ---------------------------------------------------------------------- */
 
.pm-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
 
.pm-info-cards-wrapper {
  background: var(--pm-surface);
  border: 1px solid var(--pm-border);
  border-radius: var(--pm-radius-lg);
  padding: 14px 16px;
  flex-shrink: 0;
}
 
.pm-info-cards-header {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 2px;
}
 
.pm-info-cards-subheader {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
}
 
.pm-info-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  width: 100%;
  flex-shrink: 0;
}
 
.pm-card {
  background: #f7f8fb;
  border: 1px solid var(--pm-border);
  border-radius: var(--pm-radius-md);
  padding: 12px 14px;
  min-height: 100px;
}
 
.pm-card h3 {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 6px;
}
 
.pm-card p {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
 
.pm-applies-list,
.pm-legend-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
 
.pm-applies-list li {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #64748b;
}
 
.pm-applies-list li svg {
  color: var(--pm-primary);
  flex: 0 0 auto;
}
 
.pm-legend-list li {
  display: flex;
  align-items: center;
  gap: 7px;
}
 
.pm-legend-list strong {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
}
 
.pm-legend-list small {
  display: block;
  font-size: 11px;
  color: #64748b;
}
 
/* ---------------------------------------------------------------------- */
/* Access badges                                                          */
/* ---------------------------------------------------------------------- */
 
.access-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid transparent;
  flex: 0 0 auto;
}
 
.access-full {
  background: var(--pm-green-bg);
  border-color: var(--pm-green-border);
  color: var(--pm-green-text);
}
 
.access-partial {
  background: var(--pm-amber-bg);
  border-color: var(--pm-amber-border);
  color: var(--pm-amber-text);
}
 
.access-none {
  background: var(--pm-red-bg);
  border-color: var(--pm-red-border);
  color: var(--pm-red-text);
}
 
/* ---------------------------------------------------------------------- */
/* Matrix card                                                            */
/* ---------------------------------------------------------------------- */
 
.pm-matrix-card {
  background: var(--pm-surface);
  border: 1px solid var(--pm-border);
  border-radius: var(--pm-radius-lg);
  padding: 14px 16px;
  flex-shrink: 0;
}
 
.pm-matrix-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
 
.pm-matrix-toolbar h3 {
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}
 
.pm-matrix-toolbar p {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}
 
.pm-matrix-toolbar-actions {
  display: flex;
  gap: 8px;
}
 
.pm-matrix-search {
  max-width: 260px;
  margin-bottom: 10px;
}
 
.pm-table-wrap {
  border: 1px solid var(--pm-border);
  border-radius: var(--pm-radius-md);
  overflow: hidden;
  width: 100%;
}

.pm-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.pm-table colgroup .pm-col-permission {
  width: 26%;
}

.pm-table thead th {
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 8px 10px;
  background: #fafbfc;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pm-role-label {
  display: block;
  color: #0f172a;
  text-transform: none;
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pm-role-type {
  display: block;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: none;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pm-table tbody tr {
  border-bottom: 1px solid var(--pm-border);
}

.pm-table tbody tr:last-child {
  border-bottom: none;
}

.pm-table tbody tr:hover {
  background: #fafafe;
}

.pm-table td {
  padding: 8px 10px;
  vertical-align: middle;
  overflow: hidden;
}

.pm-col-permission {
  width: 26%;
}

.pm-perm-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  overflow: hidden;
}
 
.pm-perm-icon {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--pm-bg);
  color: var(--pm-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pm-perm-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}

.pm-perm-label {
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pm-perm-desc {
  font-size: 11px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
 
.pm-cell {
  text-align: left;
}
 
.pm-matrix-footer {
  margin-top: 8px;
  font-size: 12px;
  color: #94a3b8;
}
 
/* ---------------------------------------------------------------------- */
/* Bottom row                                                              */
/* ---------------------------------------------------------------------- */
 
.pm-footer {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 14px;
  padding: 0 18px 18px;
}
 
.pm-about-card {
  display: flex;
  gap: 12px;
  background: var(--pm-primary-light);
  border: 1px solid #d9d4fc;
  border-radius: var(--pm-radius-lg);
  padding: 14px 16px;
  align-items: flex-start;
}
 
.pm-about-icon {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #fff;
  color: var(--pm-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}
 
.pm-about-card h4 {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}
 
.pm-about-card p {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}
 
.pm-help-card {
  background: var(--pm-surface);
  border: 1px solid var(--pm-border);
  border-radius: var(--pm-radius-lg);
  padding: 14px 16px;
}
 
.pm-help-card h4 {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}
 
.pm-help-card p {
  margin: 0 0 8px;
  font-size: 12px;
  color: #64748b;
}
 
.pm-help-links {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
 
.pm-help-links a {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--pm-primary);
  text-decoration: none;
}
 
.pm-help-links a:hover {
  text-decoration: underline;
}
 
/* ---------------------------------------------------------------------- */
/* Responsive                                                             */
/* ---------------------------------------------------------------------- */
 
@media (max-width: 1100px) {
  .pm-info-cards {
    grid-template-columns: 1fr 1fr;
  }
  .pm-bottom-row {
    grid-template-columns: 1fr;
  }
}
 
@media (max-width: 900px) {
  .pm-body {
    flex-direction: column;
    height: auto;
    overflow: visible;
  }
  .pm-sidebar {
    flex: 1 1 auto;
    width: 100%;
    height: auto;
  }
  .pm-main {
    height: auto;
    overflow: visible;
  }
}
`}</style>
  );
}
 
// ---------------------------------------------------------------------------
// Static data — swap these out for API data in a real integration
// ---------------------------------------------------------------------------
 
// Display-only metadata the API doesn't send -- icon per category, keyed by
// the backend's category_code so /api/v1/permission-matrix data slots in
// directly. An unrecognized category_code falls back to a neutral icon.
const CATEGORY_ICON = {
  DASHBOARD: LayoutDashboard,
  DOCUMENT_SIGNING: FileSignature,
  SIGNATURE_REQUEST: Send,
  DOCUMENT_VERIFY: ShieldCheck,
  CERTIFICATE: Fingerprint,
  USER_GROUP: Users,
  ROLE_PERMISSION: UserCog,
  AUDIT: ScrollText,
  SYSTEM: Settings,
  INTEGRATION: Code2,
  HSM: KeyRound,
};
const DEFAULT_CATEGORY_ICON = LayoutDashboard;

const slugify = (code) => code.toLowerCase().replace(/_/g, "-");
const accessSlug = (level) => (level || "NONE").toLowerCase();

const ACCESS_META = {
  full: { icon: Check, className: "access-full", label: "Full Access" },
  partial: { icon: Minus, className: "access-partial", label: "Partial Access" },
  none: { icon: X, className: "access-none", label: "No Access" },
};
 
// ---------------------------------------------------------------------------
 
// ---------------------------------------------------------------------------
// API -> UI mapping (pure functions) and data hook
// ---------------------------------------------------------------------------

const mergeRoles = (apiRoles) =>
  apiRoles.map((r) => ({
    id: slugify(r.role_code),
    role_code: r.role_code,
    label: r.name,
    type: r.is_system_role ? "System Role" : "Custom Role",
  }));

const mergePermission = (p, category, apiRoles) => ({
  id: p.permission_code.toLowerCase().replace(/_/g, "-"),
  label: p.name,
  desc: p.name,
  icon: CATEGORY_ICON[category.category_code] || DEFAULT_CATEGORY_ICON,
  // p.access only contains an entry for roles that have at least
  // one role_permissions row for this permission -- a role with
  // none yet (e.g. a freshly created custom role) is simply absent
  // rather than "NONE", so fill every known role in explicitly.
  access: Object.fromEntries(
    apiRoles.map((r) => [slugify(r.role_code), accessSlug(p.access[r.role_code])])
  ),
});

const mergeCategories = (apiCategories, apiRoles) =>
  apiCategories.map((c) => ({
    id: c.category_code.toLowerCase(),
    category_code: c.category_code,
    label: c.name,
    desc: `Permissions related to ${c.name.toLowerCase()}`,
    icon: CATEGORY_ICON[c.category_code] || DEFAULT_CATEGORY_ICON,
    permissions: c.permissions.map((p) => mergePermission(p, c, apiRoles)),
  }));

const buildMatrix = (data) => {
  const apiRoles = data.roles || [];
  return {
    roles: mergeRoles(apiRoles),
    categories: mergeCategories(data.categories || [], apiRoles),
  };
};

function usePermissionMatrix(onCategoriesLoaded) {
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Ignore results that arrive after the component has unmounted.
    const guarded = (fn) => (value) => {
      if (!cancelled) fn(value);
    };

    async function loadMatrix() {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await axios.get(`${API_URL}/api/v1/permission-matrix`);
        const matrix = buildMatrix(res.data);
        guarded(setRoles)(matrix.roles);
        guarded(setCategories)(matrix.categories);
        guarded(onCategoriesLoaded)(matrix.categories);
      } catch (err) {
        guarded(setLoadError)(true);
      } finally {
        guarded(setLoading)(false);
      }
    }

    loadMatrix();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { categories, roles, loading, loadError };
}

function AccessBadge({ level }) {
  const meta = ACCESS_META[level];
  const Icon = meta.icon;
  return (
    <span className={`access-badge ${meta.className}`} title={meta.label}>
      <Icon size={13} strokeWidth={3} />
    </span>
  );
}
 
export default function PermissionsMatrix() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("permissions");
  const [activeCategory, setActiveCategory] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const { categories, roles, loading, loadError } = usePermissionMatrix((loaded) =>
    setActiveCategory((prev) => prev || (loaded[0] && loaded[0].id))
  );

  const filteredCategories = categories.filter((c) =>
    c.label.toLowerCase().includes(categoryFilter.toLowerCase())
  );
 
  const filteredRoles = roles.filter((r) =>
    r.label.toLowerCase().includes(roleFilter.toLowerCase())
  );
 
  const currentCategory = categories.find((c) => c.id === activeCategory);

  if (loading) {
    return (
      <div className="pm-app">
        <PermissionsMatrixStyles />
        <div style={{ padding: 24 }}>Loading permissions…</div>
      </div>
    );
  }

  if (loadError || !currentCategory) {
    return (
      <div className="pm-app">
        <PermissionsMatrixStyles />
        <div style={{ padding: 24 }}>Couldn't load permissions. Please try again.</div>
      </div>
    );
  }
 
  return (
    <div className="pm-app">
      <PermissionsMatrixStyles />
 
      <div className="pm-topbar">
        <div className="pm-tabs">
          <button
            className={`pm-tab ${activeTab === "roles" ? "active" : ""}`}
            onClick={() => navigate("/roles")}
          >
            Roles
          </button>
          <button
            className={`pm-tab ${activeTab === "permissions" ? "active" : ""}`}
            onClick={() => setActiveTab("permissions")}
          >
            Permissions
          </button>
        </div>
        <div className="pm-actions">
          <button className="btn btn-ghost">
            <Upload size={14} />
            Export Matrix
          </button>
          <button className="btn btn-primary">
            <Plus size={14} />
            Create Custom Permission
          </button>
        </div>
      </div>
 
      <div className="pm-body">
        {/* Sidebar */}
        <aside className="pm-sidebar">
          <h2 className="pm-panel-title">Permission Categories</h2>
          <div className="pm-search">
            <Search size={14} className="pm-search-icon" />
            <input
              type="text"
              placeholder="Search categories..."
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />
          </div>
 
          <nav className="pm-category-list">
            {filteredCategories.map((cat) => {
              const Icon = cat.icon;
              const isActive = cat.id === activeCategory;
              return (
                <button
                  key={cat.id}
                  className={`pm-category-item ${isActive ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  <span className="pm-category-icon">
                    <Icon size={16} />
                  </span>
                  <span className="pm-category-text">
                    <span className="pm-category-label">{cat.label}</span>
                    <span className="pm-category-desc">{cat.desc}</span>
                  </span>
                  {isActive && <ChevronRight size={15} className="pm-category-chevron" />}
                </button>
              );
            })}
          </nav>
 
          <div className="pm-sidebar-footer">{categories.length} categories</div>
        </aside>
 
        {/* Main content */}
        <main className="pm-main">
          <div className="pm-info-cards-wrapper">
            <div className="pm-info-cards-header">{currentCategory.label}</div>
            <div className="pm-info-cards-subheader">Control access to dashboard, analytics and reporting features</div>
            <div className="pm-info-cards">
              <div className="pm-card">
                <h3>Permission Description</h3>
                <p>{currentCategory.desc}</p>
              </div>
 
              <div className="pm-card">
                <h3>Applies to</h3>
                <ul className="pm-applies-list">
                  <li><LayoutDashboard size={14} /> All Dashboard Modules</li>
                  <li><LayoutDashboard size={14} /> Analytics &amp; Widgets</li>
                  <li><FileSignature size={14} /> Reports &amp; Exports</li>
                </ul>
              </div>
 
              <div className="pm-card">
                <h3>Legend</h3>
                <ul className="pm-legend-list">
                  <li>
                    <AccessBadge level="full" />
                    <span><strong>Full Access</strong><small>Can view, create, edit and delete</small></span>
                  </li>
                  <li>
                    <AccessBadge level="partial" />
                    <span><strong>Partial Access</strong><small>Can view and limited actions</small></span>
                  </li>
                  <li>
                    <AccessBadge level="none" />
                    <span><strong>No Access</strong><small>No permission to access</small></span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
 
          <div className="pm-matrix-card">
            <div className="pm-matrix-toolbar">
              <div>
                <h3>Permission Matrix</h3>
                <p>Set permission level for each role</p>
              </div>
              <div className="pm-matrix-toolbar-actions">
                <button className="btn btn-outline btn-sm">Expand All</button>
                <button className="btn btn-outline btn-sm">Collapse All</button>
              </div>
            </div>
 
            <div className="pm-search pm-matrix-search">
              <Search size={14} className="pm-search-icon" />
              <input
                type="text"
                placeholder="Search roles..."
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              />
            </div>
 
            <div className="pm-table-wrap">
              <table className="pm-table">
                <colgroup>
                  <col className="pm-col-permission" />
                  {filteredRoles.map((role) => (
                    <col key={role.id} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="pm-col-permission">Permissions</th>
                    {filteredRoles.map((role) => (
                      <th key={role.id}>
                        <span className="pm-role-label">{role.label}</span>
                        <span className="pm-role-type">{role.type}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentCategory.permissions.map((perm) => {
                    const Icon = perm.icon;
                    return (
                      <tr key={perm.id}>
                        <td className="pm-col-permission">
                          <div className="pm-perm-row">
                            <span className="pm-perm-icon">
                              <Icon size={15} />
                            </span>
                            <span className="pm-perm-text">
                              <span className="pm-perm-label">{perm.label}</span>
                              <span className="pm-perm-desc">{perm.desc}</span>
                            </span>
                          </div>
                        </td>
                        {filteredRoles.map((role) => (
                          <td key={role.id} className="pm-cell">
                            <AccessBadge level={perm.access[role.id]} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
 
            <div className="pm-matrix-footer">
              Showing 1 to {currentCategory.permissions.length} of {currentCategory.permissions.length} permissions
            </div>
          </div>
 
        </main>
      </div>

      <div className="pm-footer">
        <div className="pm-about-card">
          <span className="pm-about-icon"><ShieldCheck size={18} /></span>
          <div>
            <h4>About Permissions</h4>
            <p>Permissions are inherited from roles. Users get permissions based on their assigned roles.</p>
          </div>
        </div>
        <div className="pm-help-card">
          <h4>Need Help?</h4>
          <p>Learn more about permissions and access control.</p>
          <div className="pm-help-links">
            <a href="#"><BookOpen size={14} /> View Documentation</a>
            <a href="#"><Headphones size={14} /> Contact Support</a>
          </div>
        </div>
     </div>
    </div>
  );
}